/**
 * @fileoverview Provides utilities for tracking and serializing code diffs, including a DiffTracker class and various serialization strategies. It transforms code changes into human-readable formats for context and explanation.
 */

import { diffLines } from 'diff'
import { diffToString, OnDiffReceiver, PathDiff } from '../on_diff'
import { basename } from 'path'
import { dedent, minIndentationAmount } from '@iterator/shared-common/src/dedent'
import { CoallescedChange, toCoallescedChange } from '@src/coallesce_changes'
import { getBlock } from '@src/ast'
import { truncateDiffChanges } from '@src/rewrite'

export interface DiffSerializer {
  // Convert the diff to strings to be used for LLM context.
  serialize(diff: PathDiff): string[]
  // Whether two diffs should be coallesced together before serialization. This would be true
  // if the two diffs serialized forms would highly overlap.
  shouldCoallesce(first: PathDiff, second: PathDiff): boolean
}

export class DiffTracker implements OnDiffReceiver {
  mostRecentResult: string | undefined = undefined

  private diffs: PathDiff[] = []
  private currentDiff: PathDiff | undefined = undefined

  constructor(private diffSerializer: DiffSerializer) {}

  /**
   * Add a diff to the tracker.
   */
  async onDiff(diff: PathDiff) {
    if (!this.currentDiff) {
      this.currentDiff = diff
    } else if (this.diffSerializer.shouldCoallesce(this.currentDiff, diff)) {
      this.currentDiff = {
        path: this.currentDiff.path,
        before: this.currentDiff.before,
        after: diff.after,
        changes: diffLines(this.currentDiff.before, diff.after),
      }
    } else {
      this.diffs.push(this.currentDiff)
      this.currentDiff = diff
    }
  }

  getRecentChangesTexts(limit: number = 10): string[] {
    // Don't include current diff because that's the diff being edited.
    return [...this.diffs]
      .slice(-limit)
      .map((diff) => this.diffSerializer.serialize(diff))
      .flat()
  }

  mostRecentDiff(): PathDiff | undefined {
    return this.currentDiff
  }

  mostRecentDiffText(): string | undefined {
    const diff = this.mostRecentDiff()
    return diff ? this.diffSerializer.serialize(diff).join('\n') : undefined
  }

  getRecentFiles(limit: number = 10): { path: string; content: string }[] {
    const results = [...this.diffs].slice(-limit).map((diff) => ({
      path: diff.path,
      content: diff.after,
    }))
    if (this.currentDiff) {
      results.push({
        path: this.currentDiff.path,
        content: this.currentDiff.after,
      })
    }
    return results
  }
}

/**
 * Example, changing from "return a - b" to "return a + b"
 * ```
 * math.ts
 * - return a - b
 * + return a + b
 * ```
 */
export function AdditionAndRemovalOnly(diff: PathDiff): string[] {
  const results: string[] = []

  for (let i = 0; i < diff.changes.length; i++) {
    const change = diff.changes[i]
    const nextChange = diff.changes[i + 1]
    const fileName = basename(diff.path)
    if (change && nextChange && change.removed && nextChange.added && change.value.trim() !== '') {
      results.push(
        `${fileName}\n${dedentAndAddPrefixLines(change.value, '- ')}\n${dedentAndAddPrefixLines(nextChange.value, '+ ')}`,
      )
      i++
    } else if (change && (change.added || change.removed) && change.value.trim() !== '') {
      results.push(
        `${fileName}\n${dedentAndAddPrefixLines(change.value, change.added ? '+ ' : '- ')}`,
      )
    }
  }
  return results
}

/**
 * Example, changing from "return a - b" to "return a + b"
 * ```
 * File: path/to/math.ts
 *
 * Before:
 * function calculate(a, b) {
 *   return a - b;
 * }
 *
 * After:
 * function calculate(a, b) {
 *   return a + b;
 * }
 * ```
 */
export function StatementBeforeAfter(diff: PathDiff): string[] {
  const coallescedDiff = toCoallescedChange(diff.changes)

  const results: string[] = []
  for (const change of coallescedDiff) {
    if (change.type === 'none') {
      continue
    }

    const beforeBlock = getBlock(diff.before, change.startLine)
    const afterBlock = getBlock(diff.after, change.startLine)
    results.push(
      `File: ${diff.path}\n\nBefore:\n${dedent(beforeBlock.text)}\n\nAfter:\n${dedent(afterBlock.text)}`,
    )
  }
  return results
}

export function DiffWithSurroundingLines(diff: PathDiff): string[] {
  const diffString = diffToString(diff.changes, 4)
  if (!diffString || dedent(diffString) === diff.before) {
    // TODO: diffToString should return undefined if the diff is empty but for now
    // it returns the original code.
    return []
  }
  return [
    `<code_change path="${diff.path}">\n${trimWhitespaceLines(diffString.split('\n')).join('\n')}\n</code_change>`,
  ]
}

export const CommentDiffSerializer: DiffSerializer = {
  serialize(diff: PathDiff): string[] {
    return CommentToExplainCodeChanges(diff)
  },

  // Coallesce when:
  // - diffs are from the same file
  // TODO: maybe coallesce by file will be enough actually.
  // - there are overlapping lines when both diffs are serialized.
  shouldCoallesce(first: PathDiff, second: PathDiff): boolean {
    return first.path === second.path
  },
}

export function CommentToExplainCodeChanges(diff: PathDiff): string[] {
  const truncatedChanges = truncateDiffChanges(diff.changes, 4)
  const coallescedChanges = toCoallescedChange(truncatedChanges)
  if (!coallescedChanges.some((c) => c.type !== 'none')) {
    return []
  }

  let result = ''
  let doesResultIncludeChanges = false

  const handleAddition = (change: CoallescedChange) => {
    const lines = change.value.split('\n').filter((l) => l.trim() !== '')
    if (lines.length === 0) {
      return
    }
    const indentationAmount = minIndentationAmount(lines)
    result +=
      ' '.repeat(indentationAmount) + (lines.length > 1 ? '// Added lines\n' : '// Added line\n')
    lines.forEach((line) => {
      result += line + '\n'
    })
    result += ' '.repeat(indentationAmount) + '// End of addition\n'
    doesResultIncludeChanges = true
  }

  const handleEdit = (change: CoallescedChange & { type: 'edit' }) => {
    const priorLines = change.priorValue.split('\n').filter((l) => l.trim() !== '')

    if (priorLines.length === 0) {
      handleAddition(change)
    } else {
      const indentationAmount = minIndentationAmount(priorLines)
      result +=
        ' '.repeat(indentationAmount) +
        (priorLines.length > 1 ? '// Edited lines\n' : '// Edited line\n')

      priorLines.forEach((line) => {
        result += line.slice(0, indentationAmount) + '// ' + line.slice(indentationAmount) + '\n'
      })

      result += change.value
      result += ' '.repeat(indentationAmount) + '// End of edit\n'
      doesResultIncludeChanges = true
    }
  }

  const handleRemove = (change: CoallescedChange) => {
    const lines = change.value.split('\n').filter((l) => l.trim() !== '')
    const indentationAmount = minIndentationAmount(lines)
    result +=
      ' '.repeat(indentationAmount) +
      (lines.length > 1 ? '// Removed lines\n' : '// Removed line\n')

    lines.forEach((line) => {
      result += line.slice(0, indentationAmount) + '// ' + line.slice(indentationAmount) + '\n'
    })
    result += ' '.repeat(indentationAmount) + '// End of removal\n'
    doesResultIncludeChanges = true
  }

  for (const change of coallescedChanges) {
    if (change.type === 'none') {
      result += change.value
    } else if (change.type === 'edit') {
      handleEdit(change)
    } else if (change.type === 'remove') {
      handleRemove(change)
    } else if (change.type === 'insert') {
      handleAddition(change)
    }
  }

  if (!doesResultIncludeChanges) {
    return []
  }

  return [`<code_change path="${diff.path}">\n${result}\n</code_change>`]
}

function trimWhitespaceLines(lines: string[]): string[] {
  let start = 0
  let end = lines.length - 1

  while (start < lines.length && !lines[start]!.trim()) {
    start++
  }

  while (end >= 0 && !lines[end]!.trim()) {
    end--
  }

  if (start > end) {
    return []
  }

  return lines.slice(start, end + 1)
}

function dedentAndAddPrefixLines(text: string, prefix: string): string {
  if (text.endsWith('\n')) {
    text = text.slice(0, -1)
  }
  const dedented = dedent(text)
  return dedented
    .split('\n')
    .map((line) => prefix + line)
    .join('\n')
}
